import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY } from './useGetCommunities';

export type ApiResponse_Create_Community = {
  id: number;
  name: string;
  description: string;
  rules?: string;
  require_verification: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  metadata?: Record<string, unknown>;
};

export type ApiRequest_Create_Community = {
  name: string;
  description: string;
  rules?: string;
  require_verification: boolean;
};

const METHOD = 'POST';
const ENDPOINT = 'communities';

export const useCreateCommunity = (stateFns?: MutationStateFns<ApiResponse_Create_Community>) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_Create_Community) =>
      handleApiCall<ApiResponse_Create_Community, ApiRequest_Create_Community>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {}),
        data
      ),
    onSuccess: data => {
      enqueueSnackbar(`"${data.name}" has been created successfully. You are now an admin of this community.`, { 
        variant: 'success' 
      });
      
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY(),
        refetchType: 'all',
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to create community', { variant: 'error' });

      // eslint-disable-next-line no-console
      console.error(err);
      stateFns?.onError?.(err);
    },
  });
};